import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Link as LinkIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import contentService from '../../services/contentService';

const steps = ['Nhập URL Spreadsheet', 'Xác minh quyền truy cập', 'Xem trước dữ liệu'];

const SheetsSetup = ({ open, onClose, onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationResult, setValidationResult] = useState(null);

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setSpreadsheetUrl(url);
    setError('');

    // Auto parse spreadsheet ID
    const id = contentService.parseSpreadsheetId(url);
    setSpreadsheetId(id || '');
  };

  const handleValidate = async () => {
    if (!spreadsheetId) {
      setError('Vui lòng nhập URL hoặc ID của Google Spreadsheet');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await contentService.validateSpreadsheet(spreadsheetId);
      
      if (result.success) {
        setValidationResult(result.data);
        setActiveStep(2);
      } else {
        setError(result.message || 'Không thể truy cập spreadsheet');
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Lỗi kết nối';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await contentService.saveConfig({
        spreadsheetId,
        sheetName: validationResult?.sheets?.[0]?.title || 'Sheet1',
        headerRow: 2,
      });

      if (result.success) {
        onComplete(result.data);
      } else {
        setError(result.message || 'Không thể lưu cấu hình');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Lỗi khi lưu cấu hình');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!spreadsheetId) {
        setError('Vui lòng nhập URL hoặc ID của Google Spreadsheet');
        return;
      }
      setActiveStep(1);
    }
  };

  const copyServiceEmail = () => {
    navigator.clipboard.writeText('your-service-account@your-project.iam.gserviceaccount.com');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Nhập URL hoặc ID của Google Spreadsheet mà bạn muốn kết nối:
            </Typography>

            <TextField
              fullWidth
              label="URL hoặc Spreadsheet ID"
              placeholder="https://docs.google.com/spreadsheets/d/your-spreadsheet-id/edit"
              value={spreadsheetUrl}
              onChange={handleUrlChange}
              variant="outlined"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            {spreadsheetId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Spreadsheet ID:</strong> {spreadsheetId}
                </Typography>
              </Alert>
            )}

            <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Hướng dẫn:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1. Mở Google Sheets của bạn
                <br />
                2. Copy URL từ thanh địa chỉ trình duyệt
                <br />
                3. Dán vào ô trên
              </Typography>
            </Paper>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Để ứng dụng có thể truy cập Google Sheets, bạn cần:
            </Typography>

            <Paper sx={{ p: 3, mb: 3, bgcolor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#92400e' }}>
                Chia sẻ Spreadsheet với quyền chỉnh sửa
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                1. Mở Google Sheets → Click nút <strong>"Chia sẻ"</strong> (góc trên bên phải)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                2. Trong phần "Quyền truy cập chung", chọn <strong>"Bất kỳ ai có đường liên kết"</strong>
              </Typography>
              <Typography variant="body2">
                3. Đảm bảo quyền là <strong>"Người chỉnh sửa"</strong> để cho phép web app cập nhật dữ liệu
              </Typography>
            </Paper>

            <Button
              variant="contained"
              fullWidth
              onClick={handleValidate}
              disabled={loading}
              sx={{ 
                py: 1.5, 
                bgcolor: '#06b6d4', 
                '&:hover': { bgcolor: '#0891b2' } 
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Xác minh quyền truy cập'
              )}
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            {validationResult && (
              <>
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon />
                    <Typography variant="body2">
                      Đã kết nối thành công với <strong>{validationResult.title}</strong>
                    </Typography>
                  </Box>
                </Alert>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Xem trước dữ liệu ({validationResult.totalRows} dòng):
                </Typography>

                <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mã</TableCell>
                        <TableCell>Tiêu đề</TableCell>
                        <TableCell>Ngày đăng</TableCell>
                        <TableCell>Kênh</TableCell>
                        <TableCell>Trạng thái</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationResult.preview?.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.maVideo || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography noWrap variant="body2">
                              {row.tieuDe || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.ngayDang || '-'}</TableCell>
                          <TableCell>
                            {row.kenhDang && <Chip label={row.kenhDang} size="small" />}
                          </TableCell>
                          <TableCell>
                            {row.tienDo && (
                              <Chip 
                                label={row.tienDo} 
                                size="small"
                                sx={{
                                  bgcolor: row.tienDo === 'ĐÃ ĐĂNG' ? '#d1fae5' :
                                          row.tienDo === 'CHỜ ĐĂNG' ? '#fef3c7' : '#dbeafe',
                                  color: row.tienDo === 'ĐÃ ĐĂNG' ? '#059669' :
                                         row.tienDo === 'CHỜ ĐĂNG' ? '#d97706' : '#2563eb',
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {validationResult.preview?.length === 0 && (
                  <Alert severity="info">
                    Spreadsheet chưa có dữ liệu. Bạn có thể thêm dữ liệu sau khi kết nối.
                  </Alert>
                )}
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LinkIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Kết nối Google Sheets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Thiết lập kết nối với Google Sheets để quản lý content
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ py: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} color="inherit">
          Hủy
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Quay lại
          </Button>
        )}

        {activeStep === 0 && (
          <Button 
            variant="contained" 
            onClick={handleNext}
            disabled={!spreadsheetId}
            sx={{ bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' } }}
          >
            Tiếp tục
          </Button>
        )}

        {activeStep === 2 && (
          <Button 
            variant="contained" 
            onClick={handleSaveConfig}
            disabled={loading}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Hoàn tất kết nối'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SheetsSetup;
